import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getDoc,
  writeBatch,
  increment,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

function toMillis(value) {
  if (!value || typeof value !== 'object' || !('seconds' in value)) return 0;
  return Number(value.seconds) * 1000;
}

function getHotScore(thread, now) {
  const activityAt =
    toMillis(thread.lastReplyAt) ||
    toMillis(thread.updatedAt) ||
    toMillis(thread.createdAt);
  const ageHours =
    activityAt > 0 ? (now - activityAt) / (1000 * 60 * 60) : Infinity;
  const replyCount = thread.replyCount ?? 0;
  const likesCount = thread.likesCount ?? 0;

  let freshnessScore = 0;
  if (ageHours <= 6) freshnessScore = 18;
  else if (ageHours <= 24) freshnessScore = 12;
  else if (ageHours <= 72) freshnessScore = 7;
  else if (ageHours <= 168) freshnessScore = 3;

  let engagementScore =
    Math.min(replyCount, 20) * 3 + Math.min(likesCount, 20);
  if (replyCount >= 10) engagementScore += 8;
  else if (replyCount >= 5) engagementScore += 4;
  else if (replyCount >= 2) engagementScore += 2;

  return freshnessScore + engagementScore;
}

async function getHotThreadCandidates() {
  const [snap1, snap2] = await Promise.all([
    getDocs(
      query(collection(db, 'threads'), orderBy('lastReplyAt', 'desc'), limit(24))
    ),
    getDocs(
      query(collection(db, 'threads'), orderBy('createdAt', 'desc'), limit(24))
    ),
  ]);

  const merged = new Map();
  for (const snap of [snap1, snap2]) {
    for (const item of snap.docs) {
      const thread = { id: item.id, ...item.data() };
      if (!thread.isHidden) merged.set(thread.id, thread);
    }
  }
  return Array.from(merged.values());
}

export async function getPopularThreads(limitCount = 5) {
  const candidates = await getHotThreadCandidates();
  const now = Date.now();
  return candidates
    .sort((a, b) => {
      const diff = getHotScore(b, now) - getHotScore(a, now);
      if (diff !== 0) return diff;
      return (
        (toMillis(b.lastReplyAt) || toMillis(b.updatedAt) || toMillis(b.createdAt)) -
        (toMillis(a.lastReplyAt) || toMillis(a.updatedAt) || toMillis(a.createdAt))
      );
    })
    .slice(0, limitCount);
}

export async function getRecentThreads(limitCount = 5) {
  const snap = await getDocs(
    query(
      collection(db, 'threads'),
      where('isHidden', '==', false),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    )
  );
  return snap.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function getThreadsBySubCategory(subCategoryId, limitCount = 20, lastDoc = null) {
  const constraints = [
    where('subCategoryId', '==', subCategoryId),
    where('isHidden', '==', false),
    orderBy('createdAt', 'desc'),
    limit(limitCount),
    ...(lastDoc ? [startAfter(lastDoc)] : []),
  ];
  const snap = await getDocs(query(collection(db, 'threads'), ...constraints));
  const threads = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
  const last = snap.docs[snap.docs.length - 1] ?? null;
  return { threads, lastDoc: last };
}

export async function getThreadsByCategoryIds(categoryIds, limitCount = 40) {
  const snap = await getDocs(
    query(
      collection(db, 'threads'),
      where('isHidden', '==', false),
      orderBy('createdAt', 'desc'),
      limit(160)
    )
  );
  return snap.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .filter((t) => categoryIds.includes(t.categoryId))
    .slice(0, limitCount);
}

export async function getThreadById(threadId) {
  const snap = await getDoc(doc(db, 'threads', threadId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function createThread({ title, content, categoryId, subCategoryId, isQA, authorId, authorName }) {
  const threadRef = doc(collection(db, 'threads'));
  const batch = writeBatch(db);
  batch.set(threadRef, {
    title,
    content,
    categoryId,
    subCategoryId,
    authorId,
    authorName: authorName || '匿名ユーザー',
    imageUrls: [],
    isQA,
    isResolved: false,
    isHidden: false,
    reportCount: 0,
    replyCount: 0,
    likesCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastReplyAt: serverTimestamp(),
    lastReplyUserId: authorId,
  });
  await batch.commit();
  return threadRef.id;
}

export async function createReply({ threadId, content, authorId, authorName, authorTrip = '' }) {
  const replyRef = doc(collection(db, 'threads', threadId, 'replies'));
  const threadRef = doc(db, 'threads', threadId);
  const batch = writeBatch(db);
  batch.set(replyRef, {
    content: content.trim(),
    authorId,
    authorName,
    authorTrip,
    imageUrls: [],
    isHidden: false,
    reportCount: 0,
    likesCount: 0,
    createdAt: serverTimestamp(),
  });
  batch.update(threadRef, {
    replyCount: increment(1),
    lastReplyAt: serverTimestamp(),
    lastReplyUserId: authorId,
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
  return replyRef.id;
}

export async function toggleLike(type, threadId, targetId, userId, currentLiked) {
  const batch = writeBatch(db);
  const likeRef =
    type === 'thread'
      ? doc(db, 'threads', threadId, 'likes', userId)
      : doc(db, 'threads', threadId, 'replies', targetId, 'likes', userId);
  const targetRef =
    type === 'thread'
      ? doc(db, 'threads', threadId)
      : doc(db, 'threads', threadId, 'replies', targetId);

  if (currentLiked) {
    batch.delete(likeRef);
    batch.update(targetRef, { likesCount: increment(-1) });
  } else {
    batch.set(likeRef, { userId, createdAt: serverTimestamp() });
    batch.update(targetRef, { likesCount: increment(1) });
  }
  await batch.commit();
}

export async function checkLiked(type, threadId, targetId, userId) {
  const likeRef =
    type === 'thread'
      ? doc(db, 'threads', threadId, 'likes', userId)
      : doc(db, 'threads', threadId, 'replies', targetId, 'likes', userId);
  const snap = await getDoc(likeRef);
  return snap.exists();
}
